import React, { useEffect } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { SiteLayout, ProtectedRoute } from "components/template";
import {
  useAuth,
  BreadcrumbProvider,
  CourseManagementProvider,
  CurrencyProvider,
} from "contexts";
import {
  AgentInformation,
  AgentManagement,
  Application,
  ApplicationListings,
  Authenticate,
  CompareCourse,
  CourseInformation,
  CourseManagement,
  Dashboard,
  FrequentlyAskedQuestions,
  GenuineStudent,
  InquiryManagement,
  InquiryDetails,
  InstitutionInformation,
  InstitutionManagement,
  Landing,
  Maintenance,
  Profile,
  PreApplication,
  PreScreening,
  Search,
  Services,
  Story,
  Team,
  Unauthorized,
  UnderConstruction,
  ChatPage,
  CourseIngestion,
  UsersManagement,
  ApplicationManagement,
} from "pages";
import { UserType, UserRole } from "types/Auth/AuthTypes";

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const isMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === "true";

const AppRouter: React.FC = () => {
  const { authState } = useAuth();

  const userType: UserType = authState.userType || UserType.STUDENT;

  if (isMaintenanceMode) {
    return <Maintenance />;
  }

  return (
    <BrowserRouter>
      <SiteLayout>
        <BreadcrumbProvider>
          <CurrencyProvider>
            <ScrollToTop />
            <Routes>
              <Route path="/" element={<Landing />} />
              {/* Start of Public Routes*/}
              {/* Publicly accessible. */}

              <Route path="/faq" element={<FrequentlyAskedQuestions />} />
              <Route path="/services/:type" element={<Services />} />
              <Route path="/story" element={<Story />} />
              <Route path="/team" element={<Team />} />

              {/* End of Public Routes */}

              {/* Start of Feature-Gated Links */}
              {/* Unlocks features for authorized users. */}
              <Route
                path="/search"
                element={
                  <CourseManagementProvider>
                    <Search />
                  </CourseManagementProvider>
                }
              />
              <Route
                path="/course/:id"
                element={
                  <CourseManagementProvider>
                    <CourseInformation />
                  </CourseManagementProvider>
                }
              />
              <Route path="/compare" element={<CompareCourse />} />

              <Route path="/agent/:id" element={<AgentInformation />} />
              <Route
                path="/institution/:id"
                element={<InstitutionInformation />}
              />
              {/* End of Feature-Gated Links */}

              {/* Start of Authenticated Links */}
              {/* "Requires authentication." */}

              {/* End of Authenticated Links */}

              {/* Start of Group-Restricted Links */}
              {/* Accessible to authorized users only. */}
              <Route
                path="/userProfile"
                element={
                  <ProtectedRoute
                    userId={authState.user?.["userId"]}
                    requiredGroups={[
                      UserType.STUDENT,
                      UserType.EDUBAY,
                      UserType.PARTNER,
                      UserType.PROVIDER,
                    ]}
                  >
                    <Profile userType={userType} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/chat"
                element={
                  <ProtectedRoute
                    userId={authState.user?.["userId"]}
                    requiredGroups={[
                      UserType.STUDENT,
                      UserType.EDUBAY,
                      UserType.PARTNER,
                      UserType.PROVIDER,
                    ]}
                  >
                    <ChatPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/preScreening"
                element={
                  <ProtectedRoute requiredGroups={[UserType.STUDENT]}>
                    <PreScreening />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute
                    userId={authState.user?.["userId"]}
                    requiredGroups={[
                      UserType.EDUBAY,
                      UserType.PROVIDER,
                      UserType.PARTNER,
                    ]}
                  >
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/courseIngestion"
                element={
                  <ProtectedRoute
                    requiredRoles={[UserRole.SUPERADMIN]}
                    requiredGroups={[UserType.EDUBAY]}
                  >
                    <CourseIngestion />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/courseManagement"
                element={
                  <ProtectedRoute
                    userId={authState.user?.["userId"]}
                    requiredRoles={[
                      UserRole.SUPERADMIN,
                      UserRole.OWNER,
                      UserRole.MEMBER,
                    ]}
                    requiredGroups={[UserType.EDUBAY, UserType.PROVIDER]}
                  >
                    <CourseManagementProvider>
                      <CourseManagement />
                    </CourseManagementProvider>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/usersManagement"
                element={
                  <ProtectedRoute
                    requiredRoles={[UserRole.SUPERADMIN, UserRole.OWNER]}
                    requiredGroups={[
                      UserType.EDUBAY,
                      UserType.PARTNER,
                      UserType.PROVIDER,
                    ]}
                  >
                    <UsersManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/agentManagement"
                element={
                  <ProtectedRoute
                    userId={authState.user?.["userId"]}
                    requiredRoles={[
                      UserRole.SUPERADMIN,
                      UserRole.SUPPORT,
                      UserRole.OWNER,
                      UserRole.ADMIN,
                      UserRole.MEMBER,
                    ]}
                    requiredGroups={[
                      UserType.EDUBAY,
                      UserType.PROVIDER,
                      UserType.PARTNER,
                    ]}
                  >
                    <AgentManagement />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/institutionManagement"
                element={
                  <ProtectedRoute
                    userId={authState.user?.["userId"]}
                    requiredRoles={[
                      UserRole.SUPERADMIN,
                      UserRole.SUPPORT,
                      UserRole.OWNER,
                      UserRole.ADMIN,
                      UserRole.MEMBER,
                    ]}
                    requiredGroups={[UserType.EDUBAY, UserType.PROVIDER]}
                  >
                    <InstitutionManagement />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/enquiryManagement"
                element={
                  <ProtectedRoute
                    requiredGroups={[
                      UserType.EDUBAY,
                      UserType.PARTNER,
                      UserType.PROVIDER,
                    ]}
                  >
                    <InquiryManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/enquiry/:inquiryId"
                element={
                  <ProtectedRoute
                    requiredGroups={[
                      UserType.EDUBAY,
                      UserType.PARTNER,
                      UserType.PROVIDER,
                    ]}
                  >
                    <InquiryDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/preApplication"
                element={
                  <ProtectedRoute requiredGroups={[UserType.STUDENT]}>
                    <PreApplication />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/application"
                element={
                  <ProtectedRoute requiredGroups={[UserType.STUDENT]}>
                    <Application />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/applicationManagement"
                element={
                  <ProtectedRoute
                    userId={authState.user?.["userId"]}
                    requiredGroups={[
                      UserType.STUDENT,
                      UserType.PARTNER,
                      UserType.PROVIDER,
                    ]}
                  >
                    <ApplicationListings userType={userType} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/application/:applicationId"
                element={
                  <ProtectedRoute
                    userId={authState.user?.["userId"]}
                    requiredGroups={[
                      UserType.STUDENT,
                      UserType.PARTNER,
                      UserType.PROVIDER,
                    ]}
                  >
                    <ApplicationManagement userType={userType} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/genuineStudent"
                element={
                  <ProtectedRoute requiredGroups={[UserType.STUDENT]}>
                    <GenuineStudent />
                  </ProtectedRoute>
                }
              />

              {/* End of Group-Restricted Links */}

              <Route path="/authenticate" element={<Authenticate />} />
              <Route path="/unauthorized" element={<Unauthorized />} />
              <Route
                path="/underConstruction"
                element={<UnderConstruction />}
              />
            </Routes>
          </CurrencyProvider>
        </BreadcrumbProvider>
      </SiteLayout>
    </BrowserRouter>
  );
};

export default AppRouter;
